import { useEffect, useState } from 'react';
import api from '../api';
import { SlidersHorizontal } from 'lucide-react';
import toast from 'react-hot-toast';
import ChipBankTabs from '../components/ChipBankTabs';
import { useAuth } from '../contexts/AuthContext';
import {
  ElectronicDepositPanel,
  ReceiptPrintingPanel,
  ShiftSystemPanel,
} from './Cage';

/**
 * Player Bank — Settings
 * ----------------------
 * Admin-only tab on the Player Bank tab strip. Consolidates the three
 * configurables that used to sit at the top of the Cage page:
 *   - FabiCash electronic-deposit (demo) toggle
 *   - Receipt printing (auto-print, copies, printer IP, test print)
 *   - Shift system on/off
 *
 * The panel components themselves still live in Cage.jsx (imported
 * above) so any banker UI that needs them keeps working without
 * duplication. State, fetches, and update handlers all live here.
 */
export default function PlayerBankSettings() {
  const { isAdmin } = useAuth();

  // FabiCash electronic-deposit (demo) — admin-only fetch.
  const [electronicSettings, setElectronicSettings] = useState(null);
  // Receipt printing.
  const [receiptSettings, setReceiptSettings] = useState({
    autoPrint: true,
    copies: 2,
    printerHost: '',
  });
  const [receiptSaving, setReceiptSaving] = useState(false);
  // Shift system.
  const [shiftsSettings, setShiftsSettings] = useState({ enabled: false });
  const [shiftsSaving, setShiftsSaving] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const res = await api.get('/admin/cage/electronic-deposit/settings');
        setElectronicSettings(res.data);
      } catch (e) {
        /* silent — admin will see the panel as 'unavailable' */
      }
    })();
  }, [isAdmin]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/cage/receipt-printing/settings');
        setReceiptSettings({
          autoPrint: res.data.autoPrint !== false,
          copies: res.data.copies || 2,
          printerHost: res.data.printerHost || '',
        });
      } catch (e) {
        /* keep client-side defaults */
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/cage/shifts/settings');
        setShiftsSettings({ enabled: res.data.enabled === true });
      } catch (e) {
        /* keep client-side default (disabled) */
      }
    })();
  }, []);

  const toggleElectronicDeposit = async (next) => {
    try {
      const res = await api.put('/admin/cage/electronic-deposit/settings', {
        enabled: next,
      });
      setElectronicSettings(res.data);
      toast.success(`Electronic deposit ${next ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to update');
    }
  };

  const updateReceiptSettings = async (patch) => {
    setReceiptSaving(true);
    try {
      const res = await api.put('/admin/cage/receipt-printing/settings', patch);
      setReceiptSettings({
        autoPrint: res.data.autoPrint !== false,
        copies: res.data.copies || 2,
        printerHost: res.data.printerHost || '',
      });
      toast.success('Receipt settings saved');
    } catch (err) {
      toast.error(
        err?.response?.data?.error || 'Failed to save receipt settings'
      );
    } finally {
      setReceiptSaving(false);
    }
  };

  const updateShiftsSettings = async (patch) => {
    setShiftsSaving(true);
    try {
      const res = await api.put('/admin/cage/shifts/settings', patch);
      setShiftsSettings({ enabled: res.data.enabled === true });
      toast.success(
        `Shift system ${res.data.enabled ? 'enabled' : 'disabled'}`
      );
    } catch (err) {
      toast.error(
        err?.response?.data?.error || 'Failed to save shift settings'
      );
    } finally {
      setShiftsSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <ChipBankTabs />

      <header>
        <h1 className="text-2xl font-semibold text-white flex items-center gap-2">
          <SlidersHorizontal className="text-amber-500" size={26} />
          Player Bank — Settings
        </h1>
        <p className="text-gray-400 text-sm mt-1">
          Admin controls for the Player Bank: electronic deposit, receipt
          printing, and the shift system.
        </p>
      </header>

      {electronicSettings && (
        <ElectronicDepositPanel
          settings={electronicSettings}
          onToggle={toggleElectronicDeposit}
        />
      )}

      <ReceiptPrintingPanel
        settings={receiptSettings}
        onChange={updateReceiptSettings}
        saving={receiptSaving}
      />

      <ShiftSystemPanel
        settings={shiftsSettings}
        onChange={updateShiftsSettings}
        saving={shiftsSaving}
      />
    </div>
  );
}
